import { useEffect, useState } from "react";
import { Copy, ExternalLink, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { buildProductLink } from "@/utils/productSlug";

export function resolveRestaurantMenuLinkBase(restaurant, fallbackRestaurantId) {
  const domain = (restaurant?.custom_domain || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
  const origin = domain ? `https://${domain}` : (typeof window !== "undefined" ? window.location.origin : "");
  const restaurantSlug = restaurant?.slug || restaurant?.id || fallbackRestaurantId || "";
  return { origin, restaurantSlug };
}

export function useRestaurantMenuLinkBase(authAxios, fallbackRestaurantId) {
  const [base, setBase] = useState(null);
  useEffect(() => {
    let cancelled = false;
    authAxios.get("/restaurant")
      .then((res) => { if (!cancelled) setBase(resolveRestaurantMenuLinkBase(res.data, fallbackRestaurantId)); })
      .catch(() => {
        if (!cancelled) {
          setBase({
            origin: typeof window !== "undefined" ? window.location.origin : "",
            restaurantSlug: fallbackRestaurantId || "",
          });
        }
      });
    return () => { cancelled = true; };
  }, [authAxios, fallbackRestaurantId]);
  return base;
}

function productPublicLink(origin, restaurantSlug, product) {
  if (!product?.id || !restaurantSlug || !origin) return null;
  return buildProductLink(origin, restaurantSlug, { id: product.id, name: product.name });
}

/** Copy + open public /m/.../product/... deep link (ProductLandingMode). */
export function ProductShareLink({ product, origin, restaurantSlug, variant = "icons", className = "" }) {
  const link = productPublicLink(origin, restaurantSlug, product);
  if (!link) return null;

  const copyLink = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado");
    } catch {
      toast.error("Error al copiar enlace");
    }
  };

  if (variant === "inline") {
    return (
      <div className={`mt-1 flex items-center gap-3 ${className}`}>
        <button
          type="button"
          onClick={copyLink}
          className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 hover:underline"
          title="Copiar enlace público del producto (campañas, Meta Ads)"
        >
          <LinkIcon className="w-3 h-3" /> Copiar enlace público
        </button>
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 hover:underline"
          title="Abrir menú público en este producto"
        >
          <ExternalLink className="w-3 h-3" /> Abrir
        </a>
      </div>
    );
  }

  return (
    <div className={`flex gap-0.5 ${className}`} onClick={(e) => e.stopPropagation()}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-gray-400 hover:text-blue-600"
        title="Copiar enlace público del producto"
        onClick={copyLink}
        data-testid={`product-share-copy-${product.id?.slice(-6)}`}
      >
        <Copy className="w-3.5 h-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-gray-400 hover:text-emerald-600"
        title="Abrir menú público en este producto"
        asChild
        data-testid={`product-share-open-${product.id?.slice(-6)}`}
      >
        <a href={link} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </Button>
    </div>
  );
}
